import clsx from 'clsx';
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CssModuleType } from '@utils/cssModules';

import currentCssModule from './ProductCard.module.scss';
import Typography from '../../kit/atoms/Typography';
import Link from '../../kit/atoms/Link';
import SvgHeart from '../../kit/icons/Heart';
import SvgHeartFilled from '../../kit/icons/HeartFilled';
import SvgPlus from '../../kit/icons/Plus';
import SvgMinus from '../../kit/icons/Minus';

import { IProductCard, CategoryData, Market } from '../../types';
import { addToCart } from '../../api/opti';
import {
    getPriceForMarket,
    getOriginalPrice,
    getGa4Data,
    getDiscountPercent,
    priceRound,
    getLicensedCollectionsFilterValue
} from '../../helpers';
import { NumberParam, useQueryParam } from 'use-query-params';
export interface ProductCardProps extends React.PropsWithChildren<any> {
    data: IProductCard;
    wishlist;
    market: Market;
    discount;
    handleWishlist?: (SKU: string) => void;
    isInSlider?: boolean;
    isShortVersion?: boolean;
    noMargins?: boolean;
    index?: number;
    variationsQty?: number;
    categoryData: CategoryData;
}

const ProductCard: React.FC<ProductCardProps> = ({
    data: { value, variations },
    productData,
    wishlist,
    market,
    discount,
    isInSlider = false,
    isShortVersion = false,
    noMargins = false,
    index,
    variationsQty,
    categoryData
}) => {
    const st: CssModuleType = currentCssModule;
    const { t } = useTranslation();
    const [isHovered, setIsHovered] = useState<boolean>(false);
    const [position, setPosition] = useQueryParam('position', NumberParam);
    const [isAvailable, setIsAvailable] = useState<boolean>(true);
    const [addedToWishlist, setAddedToWishlist] = useState<boolean>(false);
    const [qtyValue, setQtyValue] = useState<number>(
        market.MarketId.Value === 'EODUS' || market.MarketId.Value === 'EODCAN'
            ? 1
            : productData.Minimum
    );
    const [minimum, setMinimum] = useState<number>(productData.Minimum);
    const [price, setPrice] = useState<string>('');
    const [originalPrice, setOriginalPrice] = useState<string | null>();
    const [addToCartText, setAddToCartText] = useState<string>(
        t('/Product/Button/AddToCart')
    );
    const [currency, setCurrency] = useState<string>('');

    const memoizedPrice = useMemo(() => {
        const renderPrice = () => {
            if (
                (!productData.IsCustomized && !variations) ||
                variations?.length === 1
            ) {
                return (
                    price && parseFloat(price) > 0.00 && `$${price} ${currency}`
                );
            }

            const prices = variations.map(({ data }) =>
                getPriceForMarket(data, market.MarketName)
            );

            const allEqual = prices.every((price) => price === prices[0]);

            if (allEqual) {
                return (
                    parseFloat(prices[0]) > 0.00 &&
                    `$${priceRound(prices[0])} ${currency}`
                );
            } else {
                const filteredPrices = prices.filter((price) => price !== null);
                const minPrice = Math.min(...filteredPrices);
                const maxPrice = Math.max(...filteredPrices);

                if (discount !== 0 && !productData.No_Additional_Discounts) {
                    if (minPrice > 0.00 && maxPrice > 0.00) {
                        return `$${getDiscountPercent(
                            minPrice,
                            discount
                        )} ${currency} - $${getDiscountPercent(
                            maxPrice,
                            discount
                        )} ${currency}`;
                    }
                    if (minPrice === 0.00)
                        return `$${getDiscountPercent(
                            maxPrice,
                            discount
                        )} ${currency}`;
                    if (maxPrice === 0.00)
                        return `$${getDiscountPercent(
                            minPrice,
                            discount
                        )} ${currency}`;
                }
                if (minPrice > 0.00 && maxPrice > 0.00) {
                    return `$${priceRound(
                        minPrice
                    )} ${currency} - $${priceRound(maxPrice)} ${currency}`;
                }
                if (minPrice === 0.00)
                    return `$${priceRound(maxPrice)} ${currency}`;
                if (maxPrice === 0.00)
                    return `$${priceRound(minPrice)} ${currency}`;
            }
        };

        return renderPrice();
    }, [
        price,
        currency,
        variations,
        productData.IsCustomized,
        market,
        discount
    ]);

    const memoizedGa4Data = useMemo(() => {
        if (!memoizedPrice) return null;

        const ga4Data = () =>
            JSON.stringify(
                getGa4Data(
                    index,
                    productData,
                    value,
                    memoizedPrice,
                    qtyValue,
                    categoryData
                )
            );

        return ga4Data();
    }, [index, productData, value, memoizedPrice, qtyValue, categoryData]);

    const isInWishlist = () =>
        wishlist?.some((item) => item.Code === productData.SKU);

    const renderWishlistIcon = () => {
        if (isInWishlist() || addedToWishlist || isHovered) {
            return <SvgHeartFilled size={16} />;
        }

        return <SvgHeart size={16} />;
    };

    const getEODIncrement = (increment) => {
        return market.MarketId.Value === 'EODUS' ||
            market.MarketId.Value === 'EODCAN'
            ? 1
            : increment;
    };

    const increaseQty = () => {
        if (qtyValue < productData.Maximum) {
            setQtyValue(
                (prevValue) =>
                    prevValue + getEODIncrement(productData.Increment)
            );
        }
    };

    const dereaseQty = () => {
        if (qtyValue > minimum) {
            setQtyValue(
                (prevValue) =>
                    prevValue - getEODIncrement(productData.Increment)
            );
        }
    };

    const handleKeyPress = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const getFinalPrice = () => {
        const marketPrice = getPriceForMarket(productData, market.MarketName);
        const oldPrice = getOriginalPrice(productData, market.MarketName);

        let finalPrice = marketPrice;

        if (!oldPrice && !discount) {
            setPrice(finalPrice?.toFixed(2));
            return;
        }

        if (discount !== 0) {
            const discountAmount = (marketPrice * discount) / 100;
            finalPrice = marketPrice - discountAmount;

            if (!oldPrice) {
                setPrice(
                    priceRound(
                        !productData.No_Additional_Discounts
                            ? finalPrice
                            : marketPrice
                    )
                );
                return;
            }

            const saleDiscountAmount = (oldPrice * discount) / 100;
            const finalDiscountPrice = priceRound(
                oldPrice - saleDiscountAmount
            );
            setOriginalPrice(
              priceRound(
                !productData.No_Additional_Discounts
                    ? finalDiscountPrice
                    : oldPrice
              )
            );
            setPrice(
                priceRound(
                    !productData.No_Additional_Discounts
                        ? finalPrice
                        : marketPrice
                )
            );

            return;
        }

        if (oldPrice && !discount) {
            setOriginalPrice(oldPrice?.toFixed(2));
            setPrice(finalPrice?.toFixed(2));
        }
    };

    useEffect(() => {
        getFinalPrice();
        setCurrency(market.Currencies[0]);
        setMinimum(
            market.MarketId.Value === 'EODUS' ||
                market.MarketId.Value === 'EODCAN'
                ? 1
                : productData.Minimum
        );

        const handler = (event) => {
            if (
                event.detail.attributeName === 'isAddedToWishlist' &&
                event.detail.btnId === productData.SKU
            ) {
                setAddedToWishlist(true);
                const ga4data = getGa4Data(
                    index,
                    productData,
                    value,
                    event.detail.price,
                    qtyValue,
                    categoryData
                );
                if (Boolean(ga4data)) {
                    window.wowga4.reportAddToWishlist(
                        ga4data,
                        qtyValue,
                        window.cartHelper.GetCartCurrency()
                    );
                }
            } else if (
                event.detail.attributeName === 'isAddedToCart' &&
                event.detail.btnId === productData.SKU
            ) {
                const ga4data = getGa4Data(
                    index,
                    productData,
                    value,
                    event.detail.price,
                    qtyValue,
                    categoryData
                );
                if (Boolean(ga4data)) {
                    window.wowga4.reportAddToCart(
                        ga4data,
                        qtyValue,
                        window.cartHelper.GetCartCurrency()
                    );
                }
            }
        };

        document.addEventListener('attributeChanged', handler);

        return () => {
            document.removeEventListener('attributeChanged', handler);
        };
    }, []);

    const addToCartAction = (e) => {
        e.preventDefault();

        if (!isAvailable) return;
        addToCart(productData.SKU, qtyValue);
        setAddToCartText('Added to cart');

        const ga4data =
            Boolean(window.wowga4) && window.wowga4.getItemData(e.target);

        if (Boolean(ga4data)) {
            window.wowga4.reportAddToCart(
                ga4data,
                qtyValue,
                window.cartHelper.GetCartCurrency()
            );
        }

        setTimeout(() => {
            setAddToCartText(t('/Product/Button/AddToCart'));
        }, 1500);
    };

    const addToWishlistAction = (e) => {
        e.preventDefault();
    };

    const handleBodyScroll = () => {
        const body = document.querySelector('body');
        const scrollTop = document.documentElement.scrollTop;
        body.style.top = `-${scrollTop}px`;
        body.setAttribute('data-top', scrollTop.toString());
    };

    const onLinkClick = () => {
        // const url = new URL(window.location.href);
        setPosition(window.scrollY);
        // window.history.pushState({}, '', url.toString());
    };

    const renderButtons = () => {
        if (!productData.IsCustomized) {
            if (variationsQty === 1) {
                return (
                    <>
                        <Link
                            aria-label={addToCartText}
                            className={clsx(
                                st.btn,
                                st['btn-primary'],
                                st.addToCart,
                                'js-track-click'
                            )}
                            data-cnstrc-btn='add_to_cart'
                            data-data={productData.SKU}
                            qty-qty={qtyValue}
                            data-label-add={t('/Product/Button/AddToCart')}
                            data-label-added={t('/Product/Button/AddedToCart')}
                            {...(!productData.Availability_Filter
                                ? { 'aria-disabled': true }
                                : {})}
                            onClick={(e) => addToCartAction(e)}
                            onAuxClick={(e) => addToCartAction(e)}
                            data-cnstrc-item-id={productData.SKU}
                            data-cnstrc-item-name={value}
                            data-cnstrc-item-variation-id={
                                productData.variation_id
                            }
                            {...(productData.Availability_Filter
                                ? { 'data-cnstrc-item-price': price }
                                : {})}
                            data-ga4-item={memoizedGa4Data}
                        >
                            {addToCartText}
                        </Link>
                        <Link
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                            className={clsx(
                                st.btn,
                                st['btn-primary'],
                                st['addToWishlist'],
                                'addToWishlist'
                            )}
                            data-cnstrc-btn='add_to_wishlist'
                            data-code={productData.SKU}
                            data-price={memoizedPrice}
                            data-qty={qtyValue}
                            onClick={(e) => addToWishlistAction(e)}
                            data-ga4-item={memoizedGa4Data}
                        >
                            {renderWishlistIcon()}
                        </Link>
                    </>
                );
            } else {
                return (
                    <Link
                        aria-label={t('/Product/Button/ShowMoreOptions')}
                        className={clsx(
                            st.btn,
                            st['btn-primary'],
                            st.addToCart
                        )}
                        data-cnstrc-btn='show_more_options'
                        data-data={productData.SKU}
                        qty-qty={qtyValue}
                        href={productData.url}
                        onClick={(e) => onLinkClick()}
                    >
                        {t('/Product/Button/ShowMoreOptions')}
                    </Link>
                );
            }
        } else {
            return (
                <Link
                    aria-label={t('/Product/Button/Customize')}
                    className={clsx(st.btn, st['btn-primary'], st.addToCart)}
                    data-cnstrc-btn='customize'
                    data-data={productData.SKU}
                    qty-qty={qtyValue}
                    href={productData.url}
                    onClick={(e) => onLinkClick()}
                >
                    {t('/Product/Button/Customize')}
                </Link>
            );
        }
    };

    return (
        <div
            className={clsx(
                st['product-card'],
                isInSlider && st['in-slider'],
                isShortVersion && st['small'],
                noMargins && st['no-margins']
            )}
            data-cnstrc-item-id={productData.SKU}
            data-cnstrc-item-name={value}
            data-cnstrc-item-variation-id={productData.variation_id}
            data-cnstrc-item-price={price}
        >
            <figure className={st['product-card__image-wrap']}>
                <Link
                    aria-label={value}
                    className={clsx(
                        st['product-card__image'],
                        'js-track-click'
                    )}
                    href={productData.url}
                    onClick={() => onLinkClick()}
                >
                    <img
                        alt={value}
                        className={st['product-card__image-block']}
                        src={`${productData.image_url}?width=470&height=470&bgColor=white`}
                        loading={index > 6 ? 'lazy' : 'eager'}
                    />
                </Link>
                {productData.Case_Pack_Quantity > 0 &&
                productData[`Price_${market.MarketName}_Case pack price`] ? (
                    <div className={st['product-card__case-pack-badge']}>
                        <Typography variant='p'>
                            <i className='fal fa-box'></i>
                            {t('/Product/CasePackPricing')}
                        </Typography>
                    </div>
                ) : null}
                <div
                    className={clsx(st.quickView, 'jsQuickView js-track-click')}
                    data-toggle='modal'
                    data-target='#quickView'
                    data={productData.SKU}
                    data-productcode={`${
                        productData.id || productData.ProductID
                    }`}
                    urlquickview='/product/quickview'
                    onClick={() => handleBodyScroll()}
                    data-ga4-item={memoizedGa4Data}
                >
                    <Typography>{t('/Product/Quickview')}</Typography>
                </div>
                <div className={clsx(st.flags, st['product-card__flags'])}>
                    {productData.Flag_1_New ? (
                        <Typography className={clsx(st.flag, st['flag--new'])}>
                            New
                        </Typography>
                    ) : (
                        ''
                    )}
                    {productData.Flag_2_BestSeller ? (
                        <Typography
                            className={clsx(st.flag, st['flag--bestseller'])}
                        >
                            Best Seller
                        </Typography>
                    ) : (
                        ''
                    )}
                    {originalPrice ? (
                        <Typography className={clsx(st.flag, st['flag--sale'])}>
                            Sale
                        </Typography>
                    ) : (
                        ''
                    )}
                </div>
            </figure>
            <Typography variant='p' className={st['product-card__brand']}>
                {getLicensedCollectionsFilterValue(productData.facets)}
            </Typography>
            <Typography variant='p' className={st['product-card__name']}>
                <Link
                    aria-label={value}
                    href={productData.url}
                    className={clsx(
                        st['product-card__name-link'],
                        st['js-track-click']
                    )}
                    onClick={() => onLinkClick()}
                >
                    {value}
                </Link>
            </Typography>
            <div
                className={clsx(
                    st['product-card__price'],
                    isShortVersion && st['small']
                )}
            >
                {originalPrice &&
                    parseFloat(originalPrice) > parseFloat(price) && (
                        <Typography className={st['product-card__price--old']}>
                            ${originalPrice}&nbsp;{currency}
                        </Typography>
                    )}
                <Typography className={st['product-card__price--final']}>
                    {memoizedPrice}
                </Typography>
            </div>
            {!isShortVersion ? (
                <>
                    <Typography
                        variant='p'
                        className={clsx(
                            st['product-card__sku'],
                            !memoizedPrice?.length &&
                                st['product-card__sku--column']
                        )}
                    >
                        {!memoizedPrice?.length ? (
                            <Typography
                                className={
                                    st[
                                        'product-card__availability--not-available'
                                    ]
                                }
                            >
                                {t('/Product/NotAvailable')}
                            </Typography>
                        ) : null}
                        {t('/Shared/Sku')} {productData.SKU}&nbsp;
                        {memoizedPrice?.length ? (
                            <Typography
                                className={st['product-card__availability']}
                            >
                                {`${t('/Product/Ships')} ${
                                    productData.Availability_Filter
                                }`}
                            </Typography>
                        ) : null}
                    </Typography>

                    <div className={st['product-card__bottom']}>
                        {!productData.IsCustomized &&
                        variationsQty === 1 &&
                        memoizedPrice?.length ? (
                            <div className={st['product-card__quantity']}>
                                <label htmlFor='qty'>
                                    {t('/Product/Label/QTY')}
                                    <Typography>
                                        {t('/Product/Label/Min')} {minimum}
                                    </Typography>
                                </label>
                                <div className={st.quantity}>
                                    <SvgMinus
                                        className={clsx(
                                            st['quantity__button'],
                                            st['qty-minus']
                                        )}
                                        onClick={dereaseQty}
                                        size={15}
                                    />
                                    <label htmlFor='quantity'>{qtyValue}</label>
                                    <input
                                        id='quantity'
                                        aria-label='quantity'
                                        value={qtyValue}
                                        onKeyPress={handleKeyPress}
                                        onChange={(e) =>
                                            setQtyValue(Number(e.target.value))
                                        }
                                        min={minimum}
                                        max={productData.Maximum}
                                        step={productData.Increment}
                                        name='qty'
                                        className={st['quantity__field']}
                                    />
                                    <SvgPlus
                                        className={clsx(
                                            st['quantity__button'],
                                            st['qty-plus']
                                        )}
                                        onClick={increaseQty}
                                        size={15}
                                    />
                                </div>
                            </div>
                        ) : null}
                        <div
                            className={clsx(
                                st['product-card__buttons'],
                                st['btn-group']
                            )}
                            role='group'
                            aria-label='Cart button group'
                        >
                            {memoizedPrice?.length ? renderButtons() : null}
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    );
};

export default ProductCard;
